import cntl from "cntl";
import { useState, cloneElement, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { COLOR, FEATURED_ICON_VARIANTS, SIZE } from "../constants";

const propTypes = {
  className: PropTypes.string,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  color: PropTypes.oneOf([
    COLOR.primary,
    COLOR.gray,
    COLOR.error,
    COLOR.success,
    COLOR.warning,
    COLOR.dark,
    COLOR.primary_light,
  ]),
  size: PropTypes.oneOf([SIZE.xs, SIZE.sm, SIZE.md, SIZE.lg, SIZE.xl]),
  variant: PropTypes.oneOf([
    FEATURED_ICON_VARIANTS.dark,
    FEATURED_ICON_VARIANTS.square,
    FEATURED_ICON_VARIANTS.light_circle,
    FEATURED_ICON_VARIANTS.light_circle_outline,
  ]),
};

function FeaturedIcon({ size, color, variant, children, className }) {
  const [border, setBorder] = useState("");
  const [outline, setOutline] = useState("");
  const [rounded, setRounded] = useState("");
  const [isDark, setIsDark] = useState(false);
  const [iconSize, setIconSize] = useState("");
  const [iconColor, setIconColor] = useState("");
  const [isSquare, setIsSquare] = useState(false);
  const [hasOutline, setHasOutline] = useState("");
  const [outlineWidth, setOutlineWidth] = useState("");
  const [featuredIconSize, setFeaturedIconSize] = useState("");
  const [featuredIconColor, setFeaturedIconColor] = useState("");

  const featuredIconWrapperCn = () => cntl`
    flex
    shrink-0
    flex-row
    items-center
    justify-center
    shadow-light-xs
    dark:shadow-dark-xs
    ${border}
    ${rounded}
    ${featuredIconSize}
    ${featuredIconColor}
    ${className ? className : undefined}
    ${hasOutline ? outline : cntl`outline-none`}
  `;

  useMemo(() => {
    switch (size) {
      case SIZE.xs:
        setOutlineWidth("outline-2");
        setFeaturedIconSize("h-6 w-6");
        setIconSize("w-3 h-3 stroke-1");
        break;
      case SIZE.sm:
        setOutlineWidth("outline-4");
        setFeaturedIconSize("h-8 w-8");
        setIconSize("w-4 h-4 stroke-[1.33px]");
        break;
      case SIZE.md:
        setOutlineWidth("outline-[6px]");
        setFeaturedIconSize("h-10 w-10");
        setIconSize("w-5 h-5 stroke-[1.5px]");
        break;
      case SIZE.lg:
        setOutlineWidth("outline-8");
        setIconSize("w-6 h-6 stroke-2");
        setFeaturedIconSize("h-12 w-12");
        break;
      case SIZE.xl:
        setFeaturedIconSize("h-14 w-14");
        setOutlineWidth("outline-[10px]");
        setIconSize("w-7 h-7 stroke-[2.33px]");
        break;
      default:
        setOutlineWidth("outline-4");
        setFeaturedIconSize("h-8 w-8");
        setIconSize("w-4 h-4 stroke-[1.33px]");
        break;
    }
  }, [size]);

  const circleColor = useCallback(() => {
    switch (color) {
      case COLOR.primary:
        setBorder("border-0");
        setIconColor(isDark ? "text-primary-light-1 dark:text-primary-dark-1" : "text-primary-light-9 dark:text-primary-dark-9");
        setFeaturedIconColor(isDark ? "bg-primary-light-9 dark:bg-primary-dark-9" : "bg-primary-light-6 dark:bg-primary-dark-6");
        setOutline(
          isDark
            ? `outline ${outlineWidth} outline-primary-light-3 dark:outline-primary-dark-3`
            : `outline ${outlineWidth} utline-primary-light-3 dark:outline-primary-dark-3`
        );
        break;
      case COLOR.gray:
        setBorder("border-0");
        setIconColor(isDark ? "text-white dark:text-gray-dark-1" : "text-gray-light-11 dark:text-gray-dark-11");
        setFeaturedIconColor(isDark ? "bg-gray-light-11 dark:bg-gray-dark-11" : "bg-gray-light-4 dark:bg-gray-dark-4");
        setOutline(
          isDark
            ? `outline ${outlineWidth} outline-gray-light-3 dark:outline-gray-dark-3`
            : `outline ${outlineWidth} outline-gray-light-3 dark:outline-gray-dark-3`
        );
        break;
      case COLOR.error:
        setBorder("border-0");
        setIconColor(isDark ? "text-white" : "text-red-light-9 dark:text-red-dark-9");
        setFeaturedIconColor(isDark ? "bg-error-light-11 dark:bg-error-dark-11" : "bg-red-light-4 dark:bg-red-dark-4");
        setOutline(
          isDark
            ? `outline ${outlineWidth} outline-red-light-3 dark:outline-red-dark-3`
            : `outline ${outlineWidth} outline-red-light-3 dark:outline-red-dark-3`
        );
        break;
      case COLOR.warning:
        setBorder("border-0");
        setIconColor(isDark ? "text-white" : "text-warning-light-11");
        setFeaturedIconColor(isDark ? "bg-warning-light-10" : "bg-warning-light-3");
        setOutline(
          isDark
            ? `outline ${outlineWidth} outline-warning-light-11`
            : `outline ${outlineWidth} outline-warning-light-2`
        );
        break;
      case COLOR.success:
        setBorder("border-0");
        setIconColor(isDark ? "text-white" : "text-success-light-11");
        setFeaturedIconColor(isDark ? "bg-success-light-11" : "bg-success-light-12");
        setOutline(
          isDark
            ? `outline ${outlineWidth} outline-success-light-11`
            : `outline ${outlineWidth} outline-success-50light-2`
        );
        break;
      default:
        setBorder("border-0");
        setIconColor(isDark ? "text-white" : "text-primary-light-11");
        setFeaturedIconColor(isDark ? "bg-primary-light-11" : "bg-primary-light-12");
        setOutline(
          isDark
            ? `outline ${outlineWidth} outline-primary-light-11`
            : `outline ${outlineWidth} outline-primary-light-12`
        );
        break;
    }
  }, [color, isDark, outlineWidth]);

  const squareColor = useCallback(() => {
    switch (color) {
      case COLOR.primary_light:
        setBorder("border-0");
        setIconColor("text-primary-light-11");
        setFeaturedIconColor("bg-primary-light-2");
        break;
      case COLOR.primary:
        setBorder("border-0");
        setIconColor("text-white");
        setFeaturedIconColor("bg-primary-light-4");
        break;
      case COLOR.dark:
        setBorder("border-0");
        setIconColor("text-white");
        setFeaturedIconColor("bg-primary-light-10");
        break;
      case COLOR.gray:
        setIconColor("text-gray-light-11 dark:text-gray-dark-11");
        setFeaturedIconColor("bg-gray-light-1 dark:bg-gray-dark-1");
        setBorder("border border-gray-light-6 dark:border-gray-dark-6");
        break;
      default:
        setBorder("border-0");
        setIconColor("text-primary-light-11");
        setFeaturedIconColor("bg-primary-light-2");
        break;
    }
  }, [color]);

  useMemo(() => {
    if (!isSquare) {
      circleColor();
    } else {
      squareColor();
    }
  }, [isSquare, circleColor, squareColor]);

  useMemo(() => {
    switch (variant) {
      case FEATURED_ICON_VARIANTS.dark:
        setIsDark(true);
        setIsSquare(false);
        setHasOutline(true);
        setRounded("rounded-full");
        break;
      case FEATURED_ICON_VARIANTS.light_circle:
        setIsDark(false);
        setIsSquare(false);
        setHasOutline(false);
        setRounded("rounded-full");
        break;
      case FEATURED_ICON_VARIANTS.light_circle_outline:
        setIsDark(false);
        setIsSquare(false);
        setHasOutline(true);
        setRounded("rounded-full");
        break;
      case FEATURED_ICON_VARIANTS.square:
        setIsDark(false);
        setIsSquare(true);
        setHasOutline(false);
        setRounded("rounded-xl");
        break;
      default:
        setIsDark(false);
        setIsSquare(false);
        setHasOutline(false);
        setRounded("rounded-full");
        break;
    }
  }, [variant]);

  return (
    <div className={featuredIconWrapperCn()}>
      {cloneElement(children, {
        className: `${iconSize} ${iconColor}`,
      })}
    </div>
  );
}

FeaturedIcon.propTypes = propTypes;
export default FeaturedIcon;
